import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERRYIDS } from "../constants/queryIds";
import v4 from "uuid/v4";
import toast from "react-hot-toast";
import { formatApiError } from "../constants/apiErrorMessages";
import {
  acceptOrganizationInvitation,
  createOrganizationMembership,
  deleteOrganization,
  deleteOrganizationDocument,
  deleteOrganizationMembership,
  fetchOrganization,
  fetchOrganizationContent,
  fetchOrganizationMembers,
  fetchOrganizationRoles,
  resendMemberInvitation,
  updateOrganization,
  updateOrganizationMembership,
  uploadOrganizationFile,
} from "../Apis/organization";
import { useReducer, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import useToken from "./useToken";
import { organizationReducer } from "../components/organization/OrganizationProvider/organizationReducer";
import { getRouteUrl } from "../routers";
import {
  ROUTE_ORGANIZATION_VIEW_ALL,
  ROUTE_ORGANIZATION_VIEW_SINGLE,
} from "../routers/content/organization";

const useOrganizationQueries = () => {
  let { organizationId } = useParams();
  const csrfToken = useToken();
  const userId = useSelector((state) => state.applicationUser?.uid);
  let history = useHistory();
  const queryClient = useQueryClient();
  const [id, setId] = useState("");
  const [reFetchOrganizationMember, setReFetchOrganizationMember] =
    useState("");
  const [state, dispatch] = useReducer(organizationReducer, {
    roles: [],
    rolesById: [],
    fieldDocumentsIds: [],
    fieldDocuments: [],
    organizationMembers: {},
    organizationApplications: null,
  });
  const { fieldDocumentsIds } = state;

  //In case OrganizationPermission is used in Application Page.
  if (!organizationId) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    organizationId = urlParams.get("organization");
  }

  const redirect = (route, parameters) =>
    history.push(getRouteUrl(route, parameters));

  const organization = useQuery(
    [QUERRYIDS.fetchOrganization, { organizationId, userId }],
    fetchOrganization,
    {
      onSuccess: (data) => {
        // Check for exception in response even with 200 status
        if (data && data.exception && Object.keys(data.exception).length > 0) {
          // Handle the exception without throwing
          console.error("Organization fetch error:", data.exception);

          if (data.exception.code === "DP-403") {
            // Redirect to organizations list when user is not authorized
            toast.error(formatApiError(data.exception));
            redirect(ROUTE_ORGANIZATION_VIEW_ALL);
          } else {
            // For other exceptions, show a generic error
            toast.error(formatApiError(data.exception));
            redirect(ROUTE_ORGANIZATION_VIEW_ALL);
          }
          return; // Exit early without processing data
        }

        // Handle new API format
        if (data?.payload) {
          const org = Array.isArray(data.payload.data)
            ? data.payload.data[0]
            : data.payload;
          setId(org?.id);
          dispatch({
            type: "fetchOrganization",
            payload: data,
          });

          // Extract members from organization response
          if (org?.members && org.members.length > 0) {
            dispatch({
              type: "organizationMemberFromNewAPI",
              payload: org.members,
            });
          }

          // Extract applications from organization response
          if (org?.applications) {
            dispatch({
              type: "organizationApplicationsFromNewAPI",
              payload: org.applications,
            });
          }
        } else if (data?.data?.attributes) {
          // Old format fallback
          setId(data?.data?.attributes.drupal_internal__id);
          dispatch({
            type: "fetchOrganization",
            payload: data,
          });
        }
      },
    }
  );

  const organizationMember = useQuery(
    [QUERRYIDS.fetchOrganizationMembers, id, reFetchOrganizationMember],
    () => fetchOrganizationMembers(organizationId, id),
    {
      enabled: false, // Disable since members come from organization response now
      onSuccess: (data) => {
        if (data.data.length > 0) {
          dispatch({
            type: "organizationMember",
            payload: data,
          });
        }
      },
    }
  );

  const organizationRoles = useQuery(
    [QUERRYIDS.fetchOrganizationRoles, userId],
    () => fetchOrganizationRoles(userId),
    {
      onSuccess: (data) => {
        dispatch({
          type: "organizationRoles",
          payload: data,
        });
      },
    }
  );

  const organizationContent = useQuery(
    [QUERRYIDS.fetchOrganizationContent, id],
    () => fetchOrganizationContent(organizationId, id),
    {
      enabled: false, // Disable since applications come from organization response now
    }
  );

  const deleteAnOrganization = useMutation(
    () => deleteOrganization(organizationId, csrfToken, userId),
    {
      onSuccess: () => redirect(ROUTE_ORGANIZATION_VIEW_ALL),
    }
  );

  const uploadAnOrganizationFile = useMutation(
    (data) => uploadOrganizationFile(csrfToken, { ...data, userId }),
    {
      onSuccess: (data) => {
        // The response is now an array of { data: { id: "attachment-id" } } objects
        const documents = data.map((datum) => datum?.data?.id).filter(Boolean);
        if (documents.length === 0) {
          throw new Error("File(s) can not be uploaded.");
        }
        updateAnOrganizationFile.mutate({
          field_documents: documents.concat(fieldDocumentsIds),
        });
      },
      onError: (error) => {
        // Log the error for debugging
        console.error("Upload failed:", error);
        // The error will be available via mutation.error in the component
      },
    }
  );

  const updateAnOrganizationFile = useMutation(
    (data) => {
      const { field_documents, title, description } = data;
      return updateOrganization(
        csrfToken,
        organizationId,
        field_documents,
        title,
        description,
        userId || "00000000-0000-0000-0000-000000000000"
      );
    },
    {
      onSuccess: (data) => {
        // Check for exception in response even with 200 status
        if (data && data.exception && Object.keys(data.exception).length > 0) {
          throw new Error(JSON.stringify(data.exception));
        }

        // Invalidate queries to force refetch with updated data
        queryClient.invalidateQueries([
          QUERRYIDS.fetchOrganization,
          { organizationId, userId },
        ]);

        dispatch({
          type: "updateOrganization",
          payload: data,
        });
      },
      onError: (error) => {
        console.error("Organization update failed:", error);
      },
    }
  );

  const updateAnOrganization = useMutation(
    (data) => {
      const { field_documents, title, description } = data;
      return updateOrganization(
        csrfToken,
        organizationId,
        field_documents,
        title,
        description,
        userId || "00000000-0000-0000-0000-000000000000"
      );
    },
    {
      onSuccess: (data) => {
        // Check for exception in response even with 200 status
        if (data && data.exception) {
          // Throw error to trigger onError callback
          throw new Error(JSON.stringify(data));
        }

        // Invalidate all organization-related queries to force refetch with updated data
        queryClient.invalidateQueries([
          QUERRYIDS.fetchOrganization,
          { organizationId, userId },
        ]);
        queryClient.invalidateQueries([QUERRYIDS.fetchOrganizations]);

        redirect(ROUTE_ORGANIZATION_VIEW_SINGLE, {
          organizationId: organizationId,
        });
        dispatch({
          type: "updateOrganization",
          payload: data,
        });
      },
      onError: (error) => {
        // Error will be available via mutation.error
        console.error("Organization update failed:", error);
      },
    }
  );

  const acceptAnOrganizationInvitation = useMutation(
    (data) => {
      return acceptOrganizationInvitation(
        csrfToken,
        { ...data, organizationId },
        userId
      );
    },
    {
      onSuccess: (data) => {
        // Check for exception in response even with 200 status
        if (data && data.exception && Object.keys(data.exception).length > 0) {
          toast.error(formatApiError(data.exception));
          return;
        }

        // Refetch the organization so the member list shows the active membership
        queryClient.invalidateQueries([
          QUERRYIDS.fetchOrganization,
          { organizationId, userId },
        ]);
      },
      onError: (error) => {
        console.error("Accepting the organization invitation failed:", error);
        if (!error?.notified) {
          toast.error("Could not accept the invitation. Please try again.");
        }
      },
    }
  );

  const updateAnOrganizationMembership = useMutation(
    (data) => {
      return updateOrganizationMembership(
        csrfToken,
        { ...data, organizationId },
        userId
      );
    },
    {
      onSuccess: () => {
        // Refetch the organization to get updated member data
        queryClient.invalidateQueries([
          QUERRYIDS.fetchOrganization,
          { organizationId, userId },
        ]);
      },
    }
  );

  const deleteAnOrganizationAsInvited = useMutation(
    (data) => {
      return deleteOrganizationMembership(csrfToken, data, userId);
    },
    {
      onSuccess: () => redirect(ROUTE_ORGANIZATION_VIEW_ALL),
    }
  );

  const deleteAnOrganizationAsInviter = useMutation(
    (data) => {
      return deleteOrganizationMembership(
        csrfToken,
        { ...data, organizationId },
        userId
      );
    },
    {
      onSuccess: () => {
        // Refetch the organization to get updated member data
        queryClient.invalidateQueries([
          QUERRYIDS.fetchOrganization,
          { organizationId, userId },
        ]);
      },
    }
  );

  const createAnOrganizationMembership = useMutation(
    (data) => {
      return createOrganizationMembership(
        csrfToken,
        data,
        userId,
        state.rolesById
      );
    },
    {
      onSuccess: () => {
        setReFetchOrganizationMember(v4());
      },
      onError: (error) => {
        // postJson has already shown the Core's message (e.g. DP-403 for a member who may
        // not invite); only cover a network-level failure here.
        if (!error?.notified) {
          toast.error("Something went wrong. The invitation was not sent.");
        }
      },
    }
  );

  const resendAnMemberInvitation = useMutation((data) => {
    return resendMemberInvitation(csrfToken, data, userId);
  });

  const deleteAnOrganizationDocument = useMutation(
    (documentId) => deleteOrganizationDocument(csrfToken, documentId, userId),
    {
      onSuccess: (data) => {
        // Check for exception in response even with 200 status
        if (data && data.exception && Object.keys(data.exception).length > 0) {
          throw new Error(JSON.stringify(data.exception));
        }
        // Invalidate to refetch organization with updated documents
        queryClient.invalidateQueries([
          QUERRYIDS.fetchOrganization,
          { organizationId, userId },
        ]);
      },
      onError: (error) => {
        console.error("Document deletion failed:", error);
      },
    }
  );

  return {
    organization,
    organizationMember,
    organizationRoles,
    organizationContent,
    deleteAnOrganization,
    uploadAnOrganizationFile,
    updateAnOrganization,
    acceptAnOrganizationInvitation,
    updateAnOrganizationMembership,
    deleteAnOrganizationAsInviter,
    deleteAnOrganizationAsInvited,
    createAnOrganizationMembership,
    resendAnMemberInvitation,
    deleteAnOrganizationDocument,
    state,
  };
};

export default useOrganizationQueries;
